import { ENV } from '@/shared/config/env';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';
type LogCategory =
  | 'websocket'
  | 'api'
  | 'auth'
  | 'ui'
  | 'inference'
  | 'media'
  | 'payment'
  | 'persona'
  | 'stripe'
  | 'analytics'
  | 'general';

interface LogContext {
  [key: string]: unknown;
}

interface LogOptions {
  category?: LogCategory;
  userId?: string;
  requestId?: string;
  performance?: boolean;
}

class Logger {
  private isDevelopment = ENV.NODE_ENV === 'development';
  private enabledCategories = new Set<LogCategory>();

  constructor() {
    // In development, enable all categories by default
    if (this.isDevelopment) {
      this.enabledCategories = new Set([
        'websocket',
        'api',
        'auth',
        'ui',
        'inference',
        'media',
        'payment',
        'persona',
        'stripe',
        'analytics',
        'general',
      ]);
    }
  }

  /**
   * Configure which categories to log in development
   */
  configure(options: { categories?: LogCategory[] }) {
    if (options.categories) {
      this.enabledCategories = new Set(options.categories);
    }
  }

  private shouldLog(level: LogLevel, category: LogCategory): boolean {
    // In production/preprod: ONLY log errors and warnings
    if (!this.isDevelopment) {
      return level === 'error' || level === 'warn';
    }

    // In development: always log errors and warnings
    if (level === 'error' || level === 'warn') {
      return true;
    }

    // In development: check if category is enabled for debug/info logs
    if (!this.enabledCategories.has(category)) {
      return false;
    }

    return true;
  }

  private formatMessage(
    level: LogLevel,
    message: string,
    category: LogCategory,
    context?: LogContext,
  ): string {
    const timestamp = new Date().toISOString();
    const categoryStr = category !== 'general' ? `[${category.toUpperCase()}]` : '';
    const contextStr = context ? ` ${JSON.stringify(context, null, 2)}` : '';
    return `[${timestamp}] ${level.toUpperCase()}: ${categoryStr} ${message}${contextStr}`;
  }

  private log(level: LogLevel, message: string, options: LogOptions = {}, context?: LogContext) {
    const category = options.category || 'general';

    if (!this.shouldLog(level, category)) {
      return;
    }

    const formattedMessage = this.formatMessage(level, message, category, context);

    // Console output
    switch (level) {
      case 'debug':
        // eslint-disable-next-line no-console
        console.log(formattedMessage);
        break;
      case 'info':
        // eslint-disable-next-line no-console
        console.log(formattedMessage);
        break;
      case 'warn':
        // eslint-disable-next-line no-console
        console.warn(formattedMessage);
        break;
      case 'error':
        // eslint-disable-next-line no-console
        console.error(formattedMessage);
        break;
    }

    // Sentry integration for errors and warnings
    if (level === 'error' || level === 'warn') {
      this.sendToSentry(level, message, category, context, options);
    }

    // Add breadcrumb for debugging context
    this.addBreadcrumb(level, message, category, context);
  }

  private async sendToSentry(
    level: LogLevel,
    message: string,
    category: LogCategory,
    context?: LogContext,
    options: LogOptions = {},
  ) {
    try {
      // Dynamic import to avoid circular dependencies
      const { sentryHelpers } = await import('./sentry');

      const sentryContext = {
        category,
        level,
        ...(options.userId && { userId: options.userId }),
        ...(options.requestId && { requestId: options.requestId }),
        ...context,
      };

      if (level === 'error') {
        // If context contains an error object, use captureError, otherwise captureMessage
        const error = context?.error as Error;
        if (error instanceof Error) {
          sentryHelpers.captureError(error, sentryContext);
        } else {
          sentryHelpers.captureMessage(message, 'error');
        }
      } else if (level === 'warn') {
        sentryHelpers.captureMessage(message, 'warning');
      }
    } catch {
      // Silently fail if Sentry is not available
    }
  }

  private async addBreadcrumb(
    level: LogLevel,
    message: string,
    category: LogCategory,
    context?: LogContext,
  ) {
    try {
      // Only add breadcrumbs for significant events
      if (level === 'error' || level === 'warn' || category === 'websocket' || category === 'api') {
        const { sentryHelpers } = await import('./sentry');
        sentryHelpers.addBreadcrumb(message, category, context);
      }
    } catch {
      // Silently fail if Sentry is not available
    }
  }

  // Standard logging methods
  debug(message: string, options: LogOptions = {}, context?: LogContext) {
    this.log('debug', message, options, context);
  }

  info(message: string, options: LogOptions = {}, context?: LogContext) {
    this.log('info', message, options, context);
  }

  warn(message: string, options: LogOptions = {}, context?: LogContext) {
    this.log('warn', message, options, context);
  }

  error(message: string, options: LogOptions = {}, context?: LogContext) {
    this.log('error', message, options, context);
  }

  // Category-specific convenience methods
  websocket = {
    debug: (message: string, context?: LogContext) =>
      this.debug(message, { category: 'websocket' }, context),
    info: (message: string, context?: LogContext) =>
      this.info(message, { category: 'websocket' }, context),
    warn: (message: string, context?: LogContext) =>
      this.warn(message, { category: 'websocket' }, context),
    error: (message: string, context?: LogContext) =>
      this.error(message, { category: 'websocket' }, context),
  };

  api = {
    debug: (message: string, context?: LogContext) =>
      this.debug(message, { category: 'api' }, context),
    info: (message: string, context?: LogContext) =>
      this.info(message, { category: 'api' }, context),
    warn: (message: string, context?: LogContext) =>
      this.warn(message, { category: 'api' }, context),
    error: (message: string, context?: LogContext) =>
      this.error(message, { category: 'api' }, context),
  };

  auth = {
    debug: (message: string, context?: LogContext) =>
      this.debug(message, { category: 'auth' }, context),
    info: (message: string, context?: LogContext) =>
      this.info(message, { category: 'auth' }, context),
    warn: (message: string, context?: LogContext) =>
      this.warn(message, { category: 'auth' }, context),
    error: (message: string, context?: LogContext) =>
      this.error(message, { category: 'auth' }, context),
  };

  ui = {
    debug: (message: string, context?: LogContext) =>
      this.debug(message, { category: 'ui' }, context),
    info: (message: string, context?: LogContext) =>
      this.info(message, { category: 'ui' }, context),
    warn: (message: string, context?: LogContext) =>
      this.warn(message, { category: 'ui' }, context),
    error: (message: string, context?: LogContext) =>
      this.error(message, { category: 'ui' }, context),
  };

  media = {
    debug: (message: string, context?: LogContext) =>
      this.debug(message, { category: 'media' }, context),
    info: (message: string, context?: LogContext) =>
      this.info(message, { category: 'media' }, context),
    warn: (message: string, context?: LogContext) =>
      this.warn(message, { category: 'media' }, context),
    error: (message: string, context?: LogContext) =>
      this.error(message, { category: 'media' }, context),
  };

  inference = {
    debug: (message: string, context?: LogContext) =>
      this.debug(message, { category: 'inference' }, context),
    info: (message: string, context?: LogContext) =>
      this.info(message, { category: 'inference' }, context),
    warn: (message: string, context?: LogContext) =>
      this.warn(message, { category: 'inference' }, context),
    error: (message: string, context?: LogContext) =>
      this.error(message, { category: 'inference' }, context),
  };

  payment = {
    debug: (message: string, context?: LogContext) =>
      this.debug(message, { category: 'payment' }, context),
    info: (message: string, context?: LogContext) =>
      this.info(message, { category: 'payment' }, context),
    warn: (message: string, context?: LogContext) =>
      this.warn(message, { category: 'payment' }, context),
    error: (message: string, context?: LogContext) =>
      this.error(message, { category: 'payment' }, context),
  };

  persona = {
    debug: (message: string, context?: LogContext) =>
      this.debug(message, { category: 'persona' }, context),
    info: (message: string, context?: LogContext) =>
      this.info(message, { category: 'persona' }, context),
    warn: (message: string, context?: LogContext) =>
      this.warn(message, { category: 'persona' }, context),
    error: (message: string, context?: LogContext) =>
      this.error(message, { category: 'persona' }, context),
  };

  stripe = {
    debug: (message: string, context?: LogContext) =>
      this.debug(message, { category: 'stripe' }, context),
    info: (message: string, context?: LogContext) =>
      this.info(message, { category: 'stripe' }, context),
    warn: (message: string, context?: LogContext) =>
      this.warn(message, { category: 'stripe' }, context),
    error: (message: string, context?: LogContext) =>
      this.error(message, { category: 'stripe' }, context),
  };

  analytics = {
    debug: (message: string, context?: LogContext) =>
      this.debug(message, { category: 'analytics' }, context),
    info: (message: string, context?: LogContext) =>
      this.info(message, { category: 'analytics' }, context),
    warn: (message: string, context?: LogContext) =>
      this.warn(message, { category: 'analytics' }, context),
    error: (message: string, context?: LogContext) =>
      this.error(message, { category: 'analytics' }, context),
  };

  general = {
    debug: (message: string, context?: LogContext) =>
      this.debug(message, { category: 'general' }, context),
    info: (message: string, context?: LogContext) =>
      this.info(message, { category: 'general' }, context),
    warn: (message: string, context?: LogContext) =>
      this.warn(message, { category: 'general' }, context),
    error: (message: string, context?: LogContext) =>
      this.error(message, { category: 'general' }, context),
  };

  // Performance logging utility
  time = {
    start: (label: string, category: LogCategory = 'general') => {
      const startTime = performance.now();
      return {
        end: () => {
          const duration = performance.now() - startTime;
          this.debug(
            `${label} completed in ${duration.toFixed(2)}ms`,
            {
              category,
              performance: true,
            },
            { duration },
          );
        },
      };
    },
  };
}

export const logger = new Logger();
